import { faker } from '@faker-js/faker';
import { match } from 'ts-pattern';
import {
  AssigneeUpdate,
  NoteUpdate,
  PriorityUpdate,
  StatusUpdate,
  TeamUpdate,
} from '../entitites/task.entity';
import {
  IActivityEntity,
  IClaimEntity,
  ICustomerEntity,
  IInsuranceEntity,
  IInvoiceEntity,
  INoteEntity,
  IQuotationEntity,
  ITaskEntity,
} from '../shared';
import {
  ACTIVITY_LINKED_ITEM_TYPE,
  ACTIVITY_STATUS,
  ACTIVITY_TYPE,
  BILLING_UNIT,
  CLAIM_STATUS,
  CUSTOMER_STATUS,
  CUSTOMER_TYPE,
  INSURANCE_STATUS,
  TASK_LINKED_ITEM_TYPE,
  TASK_PRIORITY,
  TASK_STATUS,
  getAddress,
  getBroker,
  getPerson,
} from './common';
import { IBrokerEntity } from '../entitites/broker.entity';
import { ITeamEntity } from '../entitites/team.entity';

export function getCustomers(length = 1): Array<ICustomerEntity> {
  return Array.from({ length }).map(() => ({
    guid: faker.string.uuid(),
    active_since: faker.date.past().toString(),
    status: faker.helpers.arrayElement(CUSTOMER_STATUS),
    reference: faker.number.int({ min: 1_000_000, max: 9_999_999 }).toString(),
    type: CUSTOMER_TYPE[1],
    iban: faker.finance.iban(),
    billing_unit: faker.helpers.arrayElement(BILLING_UNIT),
    organization: undefined,
    address: getAddress(),
    consumer: getPerson(),
  }));
}

export function getInsurances(length = 1): Array<IInsuranceEntity> {
  return Array.from({ length }).map(() => ({
    guid: faker.string.uuid(),
    status: faker.helpers.arrayElement(INSURANCE_STATUS),
    characteristic: faker.lorem.word(),
    reference: faker.number.int({ min: 1_000_000, max: 9_999_999 }).toString(),
    title: faker.lorem.word(),
    icon: faker.lorem.word(),
    price: faker.number.int({ min: 69, max: 420 }),
    effective_date: faker.date.past().toString(),
    premium_due_date: faker.date.past().toString(),
    modified_at: faker.date.past().toString(),
    created_at: faker.date.past().toString(),
    contract_duration: faker.date.future().toString(),
    automatic_renewal: faker.datatype.boolean(),
    specs: [],
    customer: getCustomers()[0],
  }));
}

export function getClaims(length = 1): Array<IClaimEntity> {
  return Array.from({ length }).map(() => ({
    guid: faker.string.uuid(),
    reference: faker.number.int().toString(),
    status: faker.helpers.arrayElement(CLAIM_STATUS),
    detailed_status: faker.lorem.sentence(4),
    title: faker.lorem.sentence(2),
    description: faker.lorem.paragraph(1),
    insurance: {
      guid: getInsurances()[0].guid,
      reference: getInsurances()[0].reference,
      title: getInsurances()[0].title,
      icon: getInsurances()[0].icon,
    },
    customer: getCustomers()[0],
    occurred_at: faker.date.past().toString(),
    claimed_amount: faker.number.int({ min: 150, max: 20003 }),
    claim_amount_indication: faker.number.int({ min: 150, max: 20003 }),
    cause: faker.lorem.word(),
    spec: [],
    activity: faker.lorem.word(),
    attachments: null,
    broker: {
      ...getPerson(),
      avatar: '',
      job_title: '',
    },
  }));
}

export function getActivities(length = 1): Array<IActivityEntity> {
  return Array.from({ length }).map(() => ({
    guid: faker.string.uuid(),
    status: faker.helpers.arrayElement(ACTIVITY_STATUS),
    linked_item: {
      guid: faker.string.uuid(),
      type: faker.helpers.arrayElement(ACTIVITY_LINKED_ITEM_TYPE),
      label: faker.lorem.word(),
    },
    date: faker.date.past().toString(),
    created_at: faker.date.past().toString(),
    created_by: faker.person.fullName(),
    type: faker.helpers.arrayElement(ACTIVITY_TYPE),
    title: faker.lorem.sentence(2),
    subtitle: faker.lorem.sentence(5),
    content: faker.lorem.paragraph(1),
    attachments: [],
  }));
}

export function getNotes(length = 1): Array<INoteEntity> {
  return Array.from({ length }).map(() => ({
    guid: faker.string.uuid(),
    created_at: faker.date.past().toString(),
    created_by: faker.person.fullName(),
    title: faker.lorem.word(2),
    type: faker.lorem.word(1),
    content: faker.lorem.paragraph(1),
    tags: Array.from({ length: faker.number.int({ min: 2, max: 5 }) }).map(
      () => ({
        label: faker.lorem.word(),
        guid: faker.string.uuid(),
      }),
    ),
  }));
}

export function getQuotations(length = 1): Array<IQuotationEntity> {
  return Array.from({ length }).map(() => ({
    guid: faker.string.uuid(),
    created_at: faker.date.past().toString(),
    status: faker.lorem.word(1),
    reference: faker.number.int({ min: 150, max: 20003 }).toString(),
    price: faker.number.float({ min: 1, max: 1003 }),
    insurances: Array.from({
      length: faker.number.int({ min: 2, max: 5 }),
    }).map(() => ({
      title: faker.lorem.word(),
      icon: faker.lorem.word(),
    })),
  }));
}

export function getTasks(length = 1): Array<ITaskEntity> {
  return Array.from({ length }).map(() => {
    const historyItem = Array.from({
      length: faker.number.int({ min: 10, max: 25 }),
    }).map(() => ({
      type: faker.helpers.arrayElement([
        'activity',
        'priority',
        'assignee',
        'team',
        'note',
      ]),
    }));

    const history = historyItem.map((item) =>
      match(item)
        .with({ type: 'activity' }, () => getActivityUpdate())
        .with({ type: 'assignee' }, () => getAssigneeUpdate())
        .with({ type: 'priority' }, () => getPriorityUpdate())
        .with({ type: 'team' }, () => getTeamUpdate())
        .with({ type: 'note' }, () => getNoteUpdate())
        .exhaustive(),
    );
    return {
      guid: faker.string.uuid(),
      reference: faker.number
        .int({ min: 1_000_000, max: 9_999_999 })
        .toString(),
      priority: faker.helpers.arrayElement(TASK_PRIORITY),
      linked_item: {
        guid: faker.string.uuid(),
        type: faker.helpers.arrayElement(TASK_LINKED_ITEM_TYPE),
        label: faker.number.int({ min: 150, max: 20003 }).toString(),
      },
      status: faker.helpers.arrayElement(TASK_STATUS),
      title: faker.lorem.sentence(2),
      description: faker.lorem.paragraph(1),
      customer: getCustomers()[0],
      note_count: faker.number.int({ min: 1, max: 10 }),
      lead_time: 5,
      created_by: getBroker(),
      created_at: faker.date.past().toString(),
      estimated_end_date: faker.date.future().toString(),
      assignee: getBroker(),
      team: {},
      publicly_visible: faker.datatype.boolean(),
      history,
    };
  });
}

function getActivityUpdate(): StatusUpdate {
  return {
    type: 'status',
    created_at: faker.date.past().toString(),
    created_by: getBroker(),
    status: faker.helpers.arrayElement(TASK_STATUS),
  };
}

function getPriorityUpdate(): PriorityUpdate {
  return {
    type: 'priority',
    created_at: faker.date.past().toString(),
    created_by: getBroker(),
    priority: faker.helpers.arrayElement(TASK_PRIORITY),
  };
}

function getAssigneeUpdate(): AssigneeUpdate {
  return {
    type: 'assignee',
    created_at: faker.date.past().toString(),
    created_by: getBroker(),
    assignee: getBroker(),
  };
}

function getTeamUpdate(): TeamUpdate {
  return {
    type: 'team',
    created_at: faker.date.past().toString(),
    created_by: getBroker(),
    team: faker.lorem.word(),
  };
}

function getNoteUpdate(): NoteUpdate {
  return {
    type: 'note',
    created_at: faker.date.past().toString(),
    created_by: getBroker(),
    content: faker.lorem.paragraph(1),
  };
}

export function getInvoices(length = 1): Array<IInvoiceEntity> {
  return Array.from({ length }).map(() => ({
    guid: faker.string.uuid(),
    reference: faker.number.int({ min: 1_000_000, max: 9_999_999 }).toString(),
    date: faker.date.past().toString(),
    amount: faker.number.float({ min: 100, max: 10000 }),
  }));
}

export function getBrokers(length = 1): Array<IBrokerEntity> {
  return Array.from({ length }).map(() => getBroker());
}

export function getTeams(length = 1): Array<ITeamEntity> {
  return Array.from({ length }).map(() => ({
    guid: faker.string.uuid(),
    name: faker.lorem.word(),
  }));
}
